import type { MeSettings, Occurrence, Payment } from '@klopotun/contracts';
import { toCivilDate } from '@klopotun/domain';
import {
  dueWithinUpcoming,
  formatOccurrenceAmount,
  occurrenceMarkOf,
  OccurrenceRow,
  type OccurrenceMark,
} from '../../../features/occurrence-view/index.js';
import {
  formatStoredDateForPeople,
  formatVariableAmount,
} from '../../../features/payment-view/index.js';
import { EmptyState, ErrorState, List, Spinner } from '../../../shared/ui/index.js';
import styles from './upcoming-list.module.css';

const NEW_MARK: OccurrenceMark = { label: 'нова', tone: 'accent' };

const withNewMark = (payment: Payment, mark: OccurrenceMark): OccurrenceMark => {
  const isOverdue = mark.tone === 'danger';
  const showsNewMark = payment.isNew && !isOverdue;

  return showsNewMark ? NEW_MARK : mark;
};

type UpcomingListProps = {
  occurrences: Occurrence[] | undefined;
  paymentById: Map<string, Payment>;
  settings: MeSettings | undefined;
  isPending: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  /** The row itself opens the subscription; the button in it goes straight to the confirmation. */
  onOpen: (occurrence: Occurrence) => void;
  onPay: (occurrence: Occurrence) => void;
};

export const UpcomingList = ({
  occurrences,
  paymentById,
  settings,
  isPending,
  errorMessage,
  onRetry,
  onOpen,
  onPay,
}: UpcomingListProps) => {
  if (errorMessage !== null) {
    return (
      <ErrorState title="Не вдалося завантажити періоди" message={errorMessage} onRetry={onRetry} />
    );
  }

  if (isPending || occurrences === undefined || settings === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  const today = toCivilDate(new Date(), settings.timezone);
  const upcoming = dueWithinUpcoming(occurrences, today);

  if (upcoming.length === 0) {
    return (
      <EmptyState
        title="Платити поки нічого"
        description="Тут з'являться періоди, які чекають оплати."
      />
    );
  }

  return (
    <List>
      {upcoming.map((occurrence) => {
        const payment = paymentById.get(occurrence.paymentId);

        return (
          <OccurrenceRow
            key={occurrence.id}
            title={payment?.name ?? 'Підписка'}
            meta={formatStoredDateForPeople(occurrence.dueDate)}
            value={
              formatOccurrenceAmount(
                occurrence,
                payment === undefined ? undefined : formatVariableAmount(payment),
              ) ?? undefined
            }
            // The mark reads the payment for its grace days, and a wrong guess at those is a wrong
            // mark: standing in for an unknown payment with a manual one would show an auto-debit
            // period still inside its waiting window as overdue.
            mark={
              payment === undefined
                ? null
                : withNewMark(
                    payment,
                    occurrenceMarkOf({
                      occurrence,
                      kind: payment.kind,
                      today,
                      autoGraceDays: settings.autoGraceDays,
                      isFromBank: payment.isFromBank,
                    }),
                  )
            }
            onClick={() => {
              onOpen(occurrence);
            }}
            action={
              payment?.isFromBank === true
                ? undefined
                : {
                    label: 'Оплатити',
                    onSelect: () => {
                      onPay(occurrence);
                    },
                  }
            }
          />
        );
      })}
    </List>
  );
};
